import { authHeaders } from "./firebase";

type AnalyticsProperties = Record<string, string | number | boolean>;
type QueuedEvent = {
  id: string;
  name: string;
  occurredAt: string;
  platform: "web";
  sessionId: string;
  installationId: string;
  properties: AnalyticsProperties;
};

const SESSION_ID = crypto.randomUUID();
const SESSION_STARTED_AT = Date.now();
const INSTALLATION_KEY = "pearlie.web.analyticsInstallationID";
const INSTALLATION_TRACKED_KEY = "pearlie.web.analyticsInstallationTracked.v2";
const QUEUE_KEY = "pearlie.web.analyticsQueue.v2";
let flushing = false;

// Before the app was called Pearlie these keys started with "brother.". Moving
// them keeps one browser one installation, so returning visitors aren't
// counted as new ones.
try {
  for (const key of [INSTALLATION_KEY, INSTALLATION_TRACKED_KEY, QUEUE_KEY]) {
    const legacyKey = key.replace(/^pearlie\./, "brother.");
    const legacy = localStorage.getItem(legacyKey);
    if (legacy !== null && localStorage.getItem(key) === null) localStorage.setItem(key, legacy);
    localStorage.removeItem(legacyKey);
  }
} catch {
  // Storage can be unavailable in strict private modes; analytics just starts fresh.
}

function installationID(): string {
  const stored = localStorage.getItem(INSTALLATION_KEY);
  if (stored && /^[0-9a-f-]{36}$/i.test(stored)) return stored;
  const created = crypto.randomUUID();
  localStorage.setItem(INSTALLATION_KEY, created);
  return created;
}

function readQueue(): QueuedEvent[] {
  try {
    const decoded = JSON.parse(localStorage.getItem(QUEUE_KEY) ?? "[]") as QueuedEvent[];
    return Array.isArray(decoded) ? decoded.slice(-500) : [];
  } catch {
    return [];
  }
}

function writeQueue(events: QueuedEvent[]): void {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(events.slice(-500)));
}

function makeEvent(name: string, properties: AnalyticsProperties): QueuedEvent {
  return {
    id: crypto.randomUUID(),
    name,
    occurredAt: new Date().toISOString(),
    platform: "web",
    sessionId: SESSION_ID,
    installationId: installationID(),
    properties: { ...properties, visibility: document.visibilityState },
  };
}

async function flushQueue(): Promise<void> {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    let queue = readQueue();
    while (queue.length) {
      const batch = queue.slice(0, 25);
      const headers = await authHeaders();
      const response = await fetch("/api/analytics", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      });
      if (!response.ok) return;
      const sent = new Set(batch.map(event => event.id));
      queue = readQueue().filter(event => !sent.has(event.id));
      writeQueue(queue);
    }
  } catch {
    // Events remain queued and retry on the next event, online transition, or
    // visibility change. Analytics can never interrupt the product experience.
  } finally {
    flushing = false;
  }
}

export function trackEvent(
  enabled: boolean,
  name: string,
  properties: AnalyticsProperties = {},
): void {
  if (!enabled) {
    localStorage.removeItem(QUEUE_KEY);
    return;
  }
  const queue = readQueue();
  if (localStorage.getItem(INSTALLATION_TRACKED_KEY) !== "true") {
    queue.push(makeEvent("installation_created", {}));
    localStorage.setItem(INSTALLATION_TRACKED_KEY, "true");
  }
  queue.push(makeEvent(name, properties));
  writeQueue(queue);
  void flushQueue();
}

export function trackSessionStarted(enabled: boolean): void {
  trackEvent(enabled, "session_started");
}

export function trackSessionEnded(enabled: boolean): void {
  trackEvent(enabled, "session_ended", {
    duration_seconds: Math.min(86_400, Math.max(0, Math.round((Date.now() - SESSION_STARTED_AT) / 1_000))),
  });
}

window.addEventListener("online", () => { void flushQueue(); });
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void flushQueue();
});
